import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowRight, Inbox } from 'lucide-react';
import Button from '../ui/Button';
import { useToast, ToastRenderer } from '../../hooks/useToast';
import { extractApiError } from '../../utils/errors';
import type { PendingActionGroup, PendingActionItem } from '../../api/pendingActions';
import PendingActionRow, { type RowDecision } from './PendingActionRow';
import DecisionDialog from './DecisionDialog';
import DetailDialog from './DetailDialog';
import { dispatchDecision, getSourceRoute, supportsNote, requiresComment } from './dispatch';
import { submitSurveyResponse } from '../../api/PreformanceManagement/Survey';

interface PendingActionsPanelProps {
  variant: 'dashboard' | 'full';
  items: PendingActionItem[];
  countsByGroup: Partial<Record<PendingActionGroup, number>>;
  totalCount: number;
  /** Silent refetch after an action resolves (or fails as already-resolved). */
  onChanged: () => Promise<void> | void;
  /** Dashboard variant: how many rows before "View all". */
  topN?: number;
}

const GROUP_ORDER: PendingActionGroup[] = ['ForYou', 'Approvals'];

/**
 * The shared Pending Actions inbox list: "For you" pinned above "Approvals",
 * inline quick actions on every row, details always one click away.
 * Owns the decision flow (confirm dialog → domain dispatch → toast → refetch);
 * hosts pass data in and refetch on change.
 */
const PendingActionsPanel: React.FC<PendingActionsPanelProps> = ({
  variant,
  items,
  countsByGroup,
  totalCount,
  onChanged,
  topN = 5,
}) => {
  const { t } = useTranslation('requests');
  const navigate = useNavigate();
  const locale = useSelector((state: { user?: { locale?: string } }) => state.user?.locale) || 'en';
  const role = useSelector((state: { user?: { role?: string } }) => state.user?.role) || '';
  const isManager = useSelector((state: { user?: { isManager?: boolean } }) => state.user?.isManager) || false;
  const { toast, showToast, dismissToast } = useToast();

  const [decisionTarget, setDecisionTarget] = useState<{ item: PendingActionItem; positive: boolean } | null>(null);
  const [detailItem, setDetailItem] = useState<PendingActionItem | null>(null);
  const [busy, setBusy] = useState<{ key: string; decision: RowDecision } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const rowKey = (item: PendingActionItem) => `${item.source}:${item.id}`;

  const visibleItems = useMemo(() => {
    if (variant === 'full') return items;
    // Dashboard: a taste of the inbox — ForYou first (server already pins
    // blocking + sorts), capped at topN across both groups.
    return items.slice(0, topN);
  }, [items, variant, topN]);

  const grouped = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        items: visibleItems.filter((i) => i.group === group),
      })).filter((g) => g.items.length > 0),
    [visibleItems],
  );

  const runDecision = async (item: PendingActionItem, positive: boolean, note: string) => {
    setBusy({ key: rowKey(item), decision: positive ? 'positive' : 'negative' });
    setSubmitting(true);
    try {
      const result = await dispatchDecision(item, positive, note);
      if (result.errors.length === 0) {
        showToast(t('Done, removed from your pending actions'), 'success');
        setDecisionTarget(null);
      } else {
        // Already-resolved tolerance: surface the domain error, then refetch —
        // if someone else handled it, the item simply disappears.
        const err = result.errors[0] as { description?: string; message?: string };
        showToast(err?.description || err?.message || t('Something went wrong'), 'error');
        setDecisionTarget(null);
      }
    } catch (err) {
      showToast(extractApiError(err, t('Something went wrong')), 'error');
      setDecisionTarget(null);
    }
    await onChanged();
    setBusy(null);
    setSubmitting(false);
  };

  const handleDecision = (item: PendingActionItem, positive: boolean) => {
    // Terms acceptance is deliberately one tap (read first via Details).
    if (!supportsNote(item) && !requiresComment(item, positive) && item.source === 'Terms') {
      runDecision(item, positive, '');
      return;
    }
    setDecisionTarget({ item, positive });
  };

  // One deep-dive rule for both the OpenOnly CTA and Details: navigate to the
  // page where the request actually lives; the in-inbox dialog is only the
  // fallback for pageless sources (or roles the target route would bounce).
  const handleDeepDive = (item: PendingActionItem) => {
    const route = getSourceRoute(item.source, { role, isManager });
    if (route) navigate(route);
    else setDetailItem(item);
  };

  /** True when Details will navigate (drives the row's aria semantics). */
  const detailsNavigates = (item: PendingActionItem) =>
    getSourceRoute(item.source, { role, isManager }) !== undefined;

  // The monthly pulse is one 0–10 question: answer it right on the row.
  const runQuickRate = async (item: PendingActionItem, rating: number) => {
    setBusy({ key: rowKey(item), decision: `rate-${rating}` });
    setSubmitting(true);
    try {
      const result = await submitSurveyResponse({ rating });
      if (result.errors.length === 0) {
        showToast(t('Done, removed from your pending actions'), 'success');
      } else {
        // Already-resolved tolerance (e.g. answered on mobile): surface the
        // domain error, then refetch so the row disappears.
        const err = result.errors[0] as { description?: string; message?: string };
        showToast(err?.description || err?.message || t('Something went wrong'), 'error');
      }
    } catch (err) {
      showToast(extractApiError(err, t('Something went wrong')), 'error');
    }
    await onChanged();
    setBusy(null);
    setSubmitting(false);
  };

  const emptyState = (
    <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="pa-empty">
      <Inbox size={28} className="text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">{t("You're all caught up")}</p>
      <p className="text-sm text-muted-foreground">{t('Anything waiting on you will show up here.')}</p>
    </div>
  );

  return (
    <div>
      {visibleItems.length === 0
        ? emptyState
        : grouped.map(({ group, items: groupItems }) => (
            <section key={group} className="mb-4 last:mb-0" aria-label={group === 'ForYou' ? t('For you') : t('Approvals')}>
              <div className="mb-1.5 flex items-center gap-2 px-4">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {group === 'ForYou' ? t('For you') : t('Approvals')}
                </h3>
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                  {countsByGroup[group] ?? groupItems.length}
                </span>
              </div>
              <div className="divide-y divide-border">
                {groupItems.map((item) => (
                  <PendingActionRow
                    key={rowKey(item)}
                    item={item}
                    locale={locale}
                    busy={busy?.key === rowKey(item) ? busy.decision : null}
                    onDecision={handleDecision}
                    onOpen={handleDeepDive}
                    onDetails={handleDeepDive}
                    onQuickRate={runQuickRate}
                    detailsNavigates={detailsNavigates(item)}
                  />
                ))}
              </div>
            </section>
          ))}

      {variant === 'dashboard' && totalCount > visibleItems.length && (
        <div className="border-t border-border px-4 pt-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pending-actions')} data-testid="pa-view-all">
            {t('View all ({{n}})', { n: totalCount })}
            <ArrowRight size={14} className="ms-1.5 rtl:-scale-x-100" />
          </Button>
        </div>
      )}

      <DecisionDialog
        target={decisionTarget}
        submitting={submitting}
        onClose={() => setDecisionTarget(null)}
        onConfirm={(note) => {
          if (decisionTarget) runDecision(decisionTarget.item, decisionTarget.positive, note);
        }}
      />
      <DetailDialog item={detailItem} locale={locale} onClose={() => setDetailItem(null)} />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default PendingActionsPanel;
